import React, { useState, useEffect, useRef } from "react";
import { Card, Button, Tag, message, Modal, Input, Avatar, Space, Popconfirm, Spin, Empty } from "antd";
import { EnvironmentOutlined, PhoneOutlined, MessageOutlined, ArrowLeftOutlined, ArrowRightOutlined, PlayCircleOutlined, SendOutlined, UserOutlined, HomeOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { getPublicRoom, sendMessage } from "../services/api";
import { PageLoadingOverlay } from "../components/RoomLoadingScene";

const roomDetailCache = new Map();

const RoomDetailScreen = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const cachedRoom = roomDetailCache.get(String(id));
    const [room, setRoom] = useState(cachedRoom || null);
    const [loading, setLoading] = useState(!cachedRoom);
    const [loadError, setLoadError] = useState("");
    const [chatModal, setChatModal] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const [inputMessage, setInputMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [applying, setApplying] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(null);
    const chatEndRef = useRef(null);
    const applyingRef = useRef(false);
    let user = {};
    try {
        user = JSON.parse(localStorage.getItem("user") || "{}");
    } catch (error) {
        user = {};
    }

    useEffect(() => { loadRoom(Boolean(cachedRoom)); }, [id]);
    useEffect(() => { if (chatModal && room) loadChatHistory(); }, [chatModal, room]);
    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatHistory]);

    const loadRoom = async (quiet = false) => {
        try {
            if (!quiet) setLoading(true);
            if (!quiet) setLoadError("");
            const res = await getPublicRoom(id);
            if (!res || typeof res !== "object") throw new Error("房源数据异常");
            setRoom(res);
            roomDetailCache.set(String(id), res);
        } catch (e) {
            if (!quiet) {
                setRoom(null);
                setLoadError(e?.error || e?.message || "房源加载失败");
                message.error(e?.error || "房源加载失败");
            }
        } finally {
            if (!quiet) setLoading(false);
        }
    };

    const loadChatHistory = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`/api/messages/conversation/${room.user_id}`, { headers: { Authorization: "Bearer " + token } });
            const data = await res.json();
            setChatHistory(data.data || []);
        } catch (e) {}
    };

    const handleSend = async () => {
        if (!inputMessage.trim()) return;
        try {
            setSending(true);
            await sendMessage({ receiver_id: room.user_id, content: inputMessage });
            setChatHistory(prev => [...prev, { id: Date.now(), sender_id: user.id, content: inputMessage, created_at: new Date().toISOString() }]);
            setInputMessage("");
        } catch (e) { message.error("发送失败"); } finally { setSending(false); }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    const handleApplyRent = async () => {
        if (applyingRef.current) return;
        applyingRef.current = true;
        setApplying(true);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`/api/rooms/${room.id}/apply-rent`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }
            });
            const data = await res.json();
            if (res.ok) {
                message.success("租房申请已发送，请等待房东确认");
            } else {
                message.error(data.error || "申请失败");
            }
        } catch (e) { message.error("申请失败"); }
        finally { applyingRef.current = false; setApplying(false); }
    };

    const statusMap = { vacant: { color: "green", text: "空置" }, rented: { color: "red", text: "已租" } };
    const decorMap = { rough: "毛坯", simple: "简装", fine: "精装", luxury: "豪装", apartment: "公寓", community: "小区" };
    const depositMap = { one_for_one: "押一付一", two_for_one: "押二付一" };
    const feeText = value => value === null || value === undefined || value === "" ? "未填写" : `¥${Number(value).toFixed(2)}`;

    if (loading) {
        return <PageLoadingOverlay caption="正在加载房源详情，请稍候…" />;
    }

    if (!room) {
        return (
            <div style={{ minHeight: "55vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Empty description={loadError || "房源不存在或已下架"}>
                    <Button type="primary" onClick={() => navigate("/")}>返回公共池</Button>
                </Empty>
            </div>
        );
    }

    const depositText = room.deposit_method === "other" ? (room.deposit_method_other || "其他") : (depositMap[room.deposit_method] || "未填写");

    const mediaItems = [
        ...(room.images || []).map(image => ({ type: "image", url: image.url })),
        ...(room.video ? [{ type: "video", url: room.video.url }] : [])
    ];
    const previewItem = previewIndex !== null ? mediaItems[previewIndex] : null;

    return (
        <div>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>返回</Button>
            
            {/* 图片展示区域 - 最顶部，横着排列 */}
            {room.images && room.images.length > 0 && (
                <div style={{ marginBottom: 16, overflowX: "auto", whiteSpace: "nowrap", padding: "8px 0" }}>
                    <div style={{ display: "inline-flex", gap: 12 }}>
                        {room.images.map((img, index) => (
                            <div key={index} style={{ display: "inline-block", cursor: "pointer" }} onClick={() => setPreviewIndex(index)}>
                                <img 
                                    src={img.url} 
                                    alt={`房源图片 ${index + 1}`} 
                                    style={{ 
                                        width: "clamp(160px, 24vw, 240px)",
                                        height: "clamp(160px, 24vw, 240px)",
                                        aspectRatio: "1 / 1",
                                        objectFit: "cover", 
                                        borderRadius: 8
                                    }} 
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Modal
                open={previewIndex !== null}
                onCancel={() => setPreviewIndex(null)}
                footer={null}
                centered
                width="min(92vw, 960px)"
                title={previewItem ? `${previewItem.type === "video" ? "房源视频" : "房源图片"} ${previewIndex + 1} / ${mediaItems.length}` : "房源媒体"}
                styles={{ body: { padding: 0 } }}
            >
                {previewItem && (
                    <div style={{ position: "relative", minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center", background: "#111", overflow: "hidden", borderRadius: 8 }}>
                        {previewItem.type === "video" ? (
                            <video key={previewItem.url} src={previewItem.url} controls autoPlay style={{ width: "100%", maxHeight: "78vh" }} />
                        ) : (
                            <img src={previewItem.url} alt={`房源图片 ${previewIndex + 1}`} style={{ display: "block", maxWidth: "100%", maxHeight: "78vh", objectFit: "contain" }} />
                        )}
                        {mediaItems.length > 1 && <>
                            <Button
                                type="primary"
                                shape="circle"
                                size="large"
                                aria-label="上一张图片"
                                icon={<ArrowLeftOutlined />}
                                onClick={() => setPreviewIndex(index => (index - 1 + mediaItems.length) % mediaItems.length)}
                                style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", opacity: 0.88 }}
                            />
                            <Button
                                type="primary"
                                shape="circle"
                                size="large"
                                aria-label="下一张图片"
                                icon={<ArrowRightOutlined />}
                                onClick={() => setPreviewIndex(index => (index + 1) % mediaItems.length)}
                                style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", opacity: 0.88 }}
                            />
                        </>}
                    </div>
                )}
            </Modal>

            {/* 视频展示 */}
            {room.video && (
                <div onClick={() => setPreviewIndex((room.images || []).length)} style={{ marginBottom: 16, position: "relative", display: "inline-block", cursor: "pointer" }}>
                    <video 
                        src={room.video.url} 
                        muted
                        preload="metadata"
                        style={{ display: "block", width: "min(100%, 520px)", maxHeight: 400, borderRadius: 8, background: "#000" }}
                    />
                    <PlayCircleOutlined aria-label="放大播放视频" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", color: "#fff", fontSize: 56, filter: "drop-shadow(0 2px 6px rgba(0,0,0,.5))" }} />
                </div>
            )}

            <Card title={room.bedrooms + "室" + (room.living_rooms || 0) + "厅 - " + room.room_number} extra={<Tag color={statusMap[room.status]?.color}>{statusMap[room.status]?.text}</Tag>}>
                <table className="room-detail-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                        <tr><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", width: 120, color: "#8c8c8c" }}>租金</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", fontSize: 20, color: "#ff4d4f", fontWeight: "bold" }}>¥{room.rent}/月</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", width: 120, color: "#8c8c8c" }}>状态</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0" }}><Tag color={statusMap[room.status]?.color}>{statusMap[room.status]?.text}</Tag></td></tr>
                        {room.commission !== undefined && <tr><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", color: "#8c8c8c" }}>中介佣金</td><td colSpan={3} style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", fontSize: 18, color: "#d48806", fontWeight: "bold" }}>¥{Number(room.commission).toFixed(2)}</td></tr>}
                        <tr><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", color: "#8c8c8c" }}>地址</td><td colSpan={3} style={{ padding: "12px", borderBottom: "1px solid #f0f0f0" }}><EnvironmentOutlined /> {room.region_name} {room.street_name} {room.building_name} {room.room_number}</td></tr>
                        <tr><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", color: "#8c8c8c" }}>装修</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0" }}>{room.decoration ? (decorMap[room.decoration] || room.decoration) : "未填写"}</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", color: "#8c8c8c" }}>押金方式</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0" }}>{depositText}</td></tr>
                        <tr><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", color: "#8c8c8c" }}>管理费</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0" }}>{feeText(room.management_fee)}/月</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", color: "#8c8c8c" }}>电费</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0" }}>{feeText(room.electricity_price)}/度</td></tr>
                        <tr><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", color: "#8c8c8c" }}>水费</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0" }}>{feeText(room.water_price)}/方</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", color: "#8c8c8c" }}>房东</td><td style={{ padding: "12px", borderBottom: "1px solid #f0f0f0" }}>{room.landlord_name}</td></tr>
                        <tr><td style={{ padding: "12px", color: "#8c8c8c" }}>联系方式</td><td colSpan={3} style={{ padding: "12px" }}><Space>{room.landlord_phone && <><PhoneOutlined /> {room.landlord_phone}</>}</Space></td></tr>
                    </tbody>
                </table>
                <div style={{ marginTop: 24, display: "flex", justifyContent: "center", gap: 16 }}>
                    {room.status === "vacant" && (user.role === "user" || user.role === "agent") && (
                        <Popconfirm title="确定要申请租住这个房间吗？" onConfirm={handleApplyRent} okText="确定" cancelText="取消">
                            <Button type="primary" size="large" loading={applying} disabled={applying}>申请租住</Button>
                        </Popconfirm>
                    )}
                    <Button icon={<MessageOutlined />} onClick={() => setChatModal(true)}>在线联系房东</Button>
                </div>
            </Card>

            <Modal title="在线联系房东" open={chatModal} onCancel={() => setChatModal(false)} footer={null} width={500}>
                <div style={{ display: "flex", flexDirection: "column", height: 400 }}>
                    <div style={{ flex: 1, overflow: "auto", padding: 16, background: "#f5f5f5", borderRadius: 4 }}>
                        {chatHistory.length === 0 ? (
                            <div style={{ textAlign: "center", color: "#8c8c8c", padding: "40px 0" }}><MessageOutlined style={{ fontSize: 32, marginBottom: 8 }} /><p>开始和房东对话吧</p></div>
                        ) : (
                            chatHistory.map((msg, index) => (
                                <div key={msg.id || index} style={{ display: "flex", justifyContent: msg.sender_id === user.id ? "flex-end" : "flex-start", marginBottom: 12 }}>
                                    <div style={{ display: "flex", alignItems: "flex-start", flexDirection: msg.sender_id === user.id ? "row-reverse" : "row" }}>
                                        <Avatar icon={<UserOutlined />} style={{ backgroundColor: msg.sender_id === user.id ? "#1890ff" : "#f0f0f0" }} />
                                        <div style={{ maxWidth: "70%", marginLeft: msg.sender_id === user.id ? 8 : 0, marginRight: msg.sender_id === user.id ? 0 : 8 }}>
                                            <div style={{ padding: "8px 12px", borderRadius: 12, background: msg.sender_id === user.id ? "#1890ff" : "#fff", color: msg.sender_id === user.id ? "#fff" : "#262626" }}>{msg.content}</div>
                                            <div style={{ fontSize: 10, color: "#8c8c8c", marginTop: 4, textAlign: msg.sender_id === user.id ? "right" : "left" }}>{new Date(msg.created_at).toLocaleString()}</div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        <div ref={chatEndRef} />
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                        <Input value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} onPressEnter={handleSend} placeholder="输入消息..." />
                        <Button type="primary" icon={<SendOutlined />} onClick={handleSend} loading={sending}>发送</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default RoomDetailScreen;
